import { useCallback, useEffect, VFC } from 'react';
import { useDispatch } from 'react-redux';
import { findWalletProviderBySymbol } from '../datas/walletProviders';
import {
  resetBridgeWithoutToken,
  selectBridgeSlice,
  setSelectedToken,
  setSelectedFromChain,
  STORAGE_SELECTED_FROM_CHAIN_KEY,
  STORAGE_SELECTED_TOKEN_KEY,
} from '../redux/slices/bridgeSlice';
import { goPage } from '../redux/slices/historySlice';
import { logout, refreshJwtToken } from '../redux/slices/kstadiumSlice';
import { setModal } from '../redux/slices/modalSlice';
import {
  connectWallet,
  resetWallet,
  selectWalletSlice,
  setConnectedWallet,
  setDisconnectWallet,
  setFromChainWalletProvider,
  STORAGE_FROM_CHAIN_WALLET_PROVIDER_KEY,
} from '../redux/slices/walletSlice';
import { useAppSelector, useInterval } from '../hooks';
import KSTADIUM from '../services/kstadium.service';
import { removeDisposableTokenTokenInLocalStorage } from '../storage/token';

const AuthProvider: VFC = () => {
  const dispatch = useDispatch();
  const { tokens, chains, selectedFromChain, selectedToChain } =
    useAppSelector(selectBridgeSlice);
  const { sessionExpiredAt, toChainWalletProvider } =
    useAppSelector(selectWalletSlice);

  // 처음 로딩될때 KStadium JWT 토큰 갱신
  const doRefresh = useCallback(async () => {
    const accessToken = KSTADIUM.getAccessToken();
    const refreshToken = KSTADIUM.getRefreshToken();

    if (!accessToken || !refreshToken) {
      return;
    }

    dispatch(refreshJwtToken());
    try {
      await KSTADIUM.postRefreshToken();
    } catch (e) {
      if (toChainWalletProvider?.symbol) {
        // TODO : kstadium logout
        dispatch(logout());
        // TODO : connected wallet에서 kstadium 제거
        dispatch(setDisconnectWallet(toChainWalletProvider.symbol));
      }
    }
  }, [dispatch, toChainWalletProvider?.symbol]);

  useEffect(() => {
    doRefresh();
  }, [doRefresh]);

  // 세션스토리지에 저장된 이전 설정 로딩
  const loadPreset = useCallback(async () => {
    const selectedTokenSymbol = sessionStorage.getItem(
      STORAGE_SELECTED_TOKEN_KEY,
    );
    if (selectedTokenSymbol) {
      const foundToken = tokens.find(
        (token) => token.symbol === selectedTokenSymbol,
      );
      if (foundToken) {
        dispatch(setSelectedToken(foundToken));
      }
    }

    const selectedFromChainSymbol = sessionStorage.getItem(
      STORAGE_SELECTED_FROM_CHAIN_KEY,
    );
    const fromChainWalletProviderSymbol = sessionStorage.getItem(
      STORAGE_FROM_CHAIN_WALLET_PROVIDER_KEY,
    );
    if (selectedFromChainSymbol && fromChainWalletProviderSymbol) {
      const foundChain = chains.find(
        (chain) => chain.symbol === selectedFromChainSymbol,
      );
      const foundWalletProvider = findWalletProviderBySymbol(
        fromChainWalletProviderSymbol,
      );
      if (foundChain && foundWalletProvider) {
        try {
          const connectResult = await connectWallet({
            chain: foundChain,
            provider: foundWalletProvider,
          });
          dispatch(setSelectedFromChain(foundChain));
          dispatch(setFromChainWalletProvider(foundWalletProvider));
          dispatch(setConnectedWallet(connectResult));
        } catch (e) {
          console.warn(`Wallet auto connection failed`);
        }
      }
    }
  }, [chains, dispatch, tokens]);

  useEffect(() => {
    loadPreset();
  }, [loadPreset]);

  // 메타마스크 이벤트 리스너
  useEffect(() => {
    const { ethereum } = window;
    const metamaskProvider = findWalletProviderBySymbol(`METAMASK`);

    if (ethereum && metamaskProvider) {
      const onAccountChanged = (accounts) => {
        if (accounts.length) {
          dispatch(
            setConnectedWallet({
              walletProvider: metamaskProvider,
              walletAddress: accounts[0],
            }),
          );
          dispatch(goPage(1));
        } else {
          dispatch(resetBridgeWithoutToken());
          dispatch(resetWallet());
        }
      };
      const onChainChanged = (chainId) => {
        if (!selectedFromChain) return;
        if (selectedFromChain.evmChainId !== Number(chainId)) {
          dispatch(setModal(`NetworkAlert`));
          dispatch(resetBridgeWithoutToken());
        }
      };

      ethereum.on(`accountsChanged`, onAccountChanged);
      ethereum.on(`chainChanged`, onChainChanged);

      return () => {
        ethereum.removeListener(`accountsChanged`, onAccountChanged);
        ethereum.removeListener(`chainChanged`, onChainChanged);
      };
    }

    return () => null;
  }, [dispatch, selectedFromChain]);

  useEffect(() => {
    removeDisposableTokenTokenInLocalStorage();
  }, []);

  useInterval(
    async () => {
      try {
        if (sessionExpiredAt <= new Date().getTime()) {
          dispatch(setModal(`TimeoutLogout`));
        }
      } catch (e) {
        // mute
      }
    },
    selectedFromChain || selectedToChain ? 5000 : null,
  );

  return <></>;
};

export default AuthProvider;
